/* eslint-disable no-plusplus */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;

  const parsed = parseFloat(
    String(value).replace('S/', '').replace(/\s/g, '').replace(',', '.'),
  );

  // eslint-disable-next-line no-restricted-globals
  return isNaN(parsed) ? 0 : parsed;
};

const formatMoney = (amount) =>
  `S/ ${(Math.round(amount * 100) / 100).toFixed(2)}`;

const getRowKey = (row, index) =>
  row.id !== null && row.id !== undefined
    ? String(row.id)
    : `${row.chargeType || 'ADDITIONAL'}-${index}`;

const getChargeTypeName = (chargeType) => {
  switch (chargeType) {
    case 'FUNDITION_ENTRY':
      return 'Ingreso a fundición';

    default:
      return chargeType || 'Cargo adicional';
  }
};

const AdditionalChargeSelectorTable = ({ rows, onChange }) => {
  const [selectedByKey, setSelectedByKey] = useState({});

  useEffect(() => {
    setSelectedByKey((previous) => {
      const next = {};
      let changed = false;

      for (let index = 0; index < rows.length; index++) {
        const key = getRowKey(rows[index], index);
        next[key] = previous[key] === true;

        if (previous[key] === undefined) {
          changed = true;
        }
      }

      if (Object.keys(previous).length !== rows.length) {
        changed = true;
      }

      return changed ? next : previous;
    });
  }, [rows]);

  const isAllChecked = useMemo(() => {
    if (rows.length === 0) return false;

    for (let index = 0; index < rows.length; index++) {
      const key = getRowKey(rows[index], index);

      if (!selectedByKey[key]) {
        return false;
      }
    }

    return true;
  }, [rows, selectedByKey]);

  const toggleAll = () => {
    const next = {};

    for (let index = 0; index < rows.length; index++) {
      const key = getRowKey(rows[index], index);
      next[key] = !isAllChecked;
    }

    setSelectedByKey(next);
  };

  const toggleRow = (row, index) => {
    const key = getRowKey(row, index);

    setSelectedByKey((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const computed = useMemo(() => {
    let globalTotal = 0;
    let selectedTotal = 0;

    const selectedRows = [];

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      const key = getRowKey(row, index);
      const amount = toNumber(row.amount);
      const applied = selectedByKey[key] === true;

      globalTotal += amount;

      if (applied) {
        selectedTotal += amount;
      }

      selectedRows.push({
        id: row.id,
        chargeType: row.chargeType,
        description: row.description,
        amount,
        applied,
      });
    }

    return {
      selectedRows,
      globalTotal: Math.round(globalTotal * 100) / 100,
      selectedTotal: Math.round(selectedTotal * 100) / 100,
    };
  }, [rows, selectedByKey]);

  const lastEmitRef = useRef({
    signature: '',
    globalTotal: null,
    selectedTotal: null,
  });

  useEffect(() => {
    if (typeof onChange !== 'function') return;

    const signature = computed.selectedRows
      .map(
        (row, index) =>
          // eslint-disable-next-line react/prop-types
          `${getRowKey(row, index)}:${row.applied ? 'selected' : 'none'}`,
      )
      .join('|');

    const previous = lastEmitRef.current;

    if (
      previous.signature === signature &&
      previous.globalTotal === computed.globalTotal &&
      previous.selectedTotal === computed.selectedTotal
    ) {
      return;
    }

    lastEmitRef.current = {
      signature,
      globalTotal: computed.globalTotal,
      selectedTotal: computed.selectedTotal,
    };

    onChange(computed);
  }, [computed, onChange]);

  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h3>Cargos adicionales</h3>

      <div className="table-responsive">
        <table className="table table-bordered align-middle mb-0">
          <thead>
            <tr>
              <th style={{ minWidth: 100, textAlign: 'center' }}>
                <div>Todos</div>
                <input
                  type="checkbox"
                  checked={isAllChecked}
                  onChange={toggleAll}
                />
              </th>

              <th style={{ minWidth: 220 }}>Tipo</th>

              <th style={{ minWidth: 140, textAlign: 'right' }}>Monto</th>

              <th style={{ minWidth: 120, textAlign: 'center' }}>Liquidar</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => {
              const key = getRowKey(row, index);
              const amount = toNumber(row.amount);

              return (
                <tr key={key}>
                  <td style={{ textAlign: 'center' }}>{index + 1}</td>

                  <td>{getChargeTypeName(row.chargeType)}</td>

                  <td style={{ textAlign: 'right' }}>{formatMoney(amount)}</td>

                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedByKey[key] === true}
                      onChange={() => toggleRow(row, index)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

AdditionalChargeSelectorTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      chargeType: PropTypes.string.isRequired,
      description: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  onChange: PropTypes.func,
};

AdditionalChargeSelectorTable.defaultProps = {
  rows: [],
  onChange: null,
};

export default AdditionalChargeSelectorTable;
